import React, { useState } from 'react';

import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';

import debounce from 'lodash/debounce';

import { css } from 'emotion';

import Modal, {
    ModalHeader,
    ModalContent,
    ModalActions,
    MODAL_CLOSE_TIMEOUT
} from '@vromo/ui/components/Modal';
import Title from '@vromo/ui/components/Title';
import Button from '@vromo/ui/components/Button';
import { qaLabelModals } from './../../../common-qa-labels/common';

const ModalsContext = React.createContext({
    confirm: (title: string, message: string, multiline?: boolean) => Promise.resolve(false)
});

export default observer(function Modals({ children }) {
    const [modals] = useState(observable.array());

    function confirm(title: string, message: string, multiline = false, destructive = false) {
        let accept: (value: boolean) => void;

        const promise = new Promise<boolean>((resolve) => {
            accept = resolve;
        });

        modals.push([title, message, multiline, destructive, (result: boolean) => accept(result)]);

        return promise;
    }

    const [title, message, multiline, destructive] = modals.length ? modals[0] : [];
    return (
        <ModalsContext.Provider value={{ confirm }}>
            <>
                {children}
                <Modal
                    isOpen={modals.length !== 0}
                    important={css`
                        z-index: 40000;
                        ${multiline ? 'white-space: pre-wrap' : null};
                    `}
                    onRequestClose={() => modals.shift()[4](false)}>
                    <ModalHeader>
                        <Title data-testid={qaLabelModals.modalTitle} style={{ marginTop: 0, fontSize: '2.0rem' }}>{title}</Title>
                    </ModalHeader>
                    <ModalContent>
                        <p data-testid={qaLabelModals.modalParagraph} style={{ fontSize: 14 }}>{message}</p>
                    </ModalContent>
                    <ModalActions>
                        <Button
                            onClick={() => modals.shift()[4](false)}
                            data-testid={qaLabelModals.modalCancelButton}>
                            Cancel
                        </Button>
                        <Button
                            destructive={destructive}
                            onClick={() => modals.shift()[4](true)}
                            data-testid={qaLabelModals.modalOkButton}>
                            OK
                        </Button>
                    </ModalActions>
                </Modal>
            </>
        </ModalsContext.Provider>
    );
});

export function useModals() {
    return React.useContext(ModalsContext);
}
